/**
 * What a market shows a provider about demand and about his own shift (D694; owner decision 2026-10-23, gap 19 of the 99/Uber/Machine audit) - governed key
 * `driver.demandHints`.
 *
 *  - HOT ZONES: the cells of a grid where several DIFFERENT riders asked for a ride in the last `windowMinutes`, drawn on the provider's map. Privacy is the
 *    point: a cell is shown only when at least `minDistinctRiders` different people asked from it (k-anonymity; the floor is
 *    {@link DEMAND_HINTS_MIN_RIDERS_FLOOR}), only the cell and a low/medium/high level leave the server (never a count, never a point), and the cells are
 *    `cellSizeMeters` wide. `radiusMeters` keeps the answer around the provider (he is shown the cells near him, at most `maxZones`) and `refreshSeconds` is how
 *    often the app asks again (the server also answers every provider of a market from one aggregate per refresh).
 *  - SHIFT HEALTH: after `nudgeAfterOnlineMinutes` online the app suggests a break, and again every `repeatEveryMinutes`. It is a suggestion - nothing is
 *    blocked and no ride is withheld.
 *
 * Shape: `{ hotZones: { enabled, windowMinutes, cellSizeMeters, minDistinctRiders, maxZones, radiusMeters, refreshSeconds },
 *           shiftHealth: { enabled, nudgeAfterOnlineMinutes, repeatEveryMinutes } }`.
 *
 * Placement: country, state and market scope, four eyes on every change, versioned, effective-dated and content-hashed. A lower scope may only TIGHTEN what is
 * a privacy or wellbeing control of the layers above it (AGENTS: a lower scope never loosens a country-level privacy or safety constraint): the reader folds
 * the country (and the state) into the deciding value - the zones stay off if a layer above switched them off, `minDistinctRiders` and `cellSizeMeters` can only
 * go UP, the shift nudge cannot be switched off where a layer above switched it on and cannot come LATER or less often than the layers above ask. An UNSET
 * market reads {@link DEFAULT_DRIVER_DEMAND_HINTS}; a version the reader cannot verify or parse reads {@link STRICT_DRIVER_DEMAND_HINTS} (no zones, the nudge on) with
 * `DRIVER_DEMAND_HINTS_INVALID`. Neutral: no country, city or currency is named here.
 */
import { policySetting, resolvePolicy } from './policy.js';
import { settingSnapshotFields } from './setting-kernel.js';
import { SCOPE_REF_KIND, ancestorLayers, marketSettingContext, settingScopeRefIn, settingScopeRefValid } from './setting-context.js';
/** The governed key. */
export const DRIVER_DEMAND_HINTS_KEY = 'driver.demandHints';
/** The fewest different riders a cell may be shown with: no scope can go below it (the database predicate holds the same number). */
export const DEMAND_HINTS_MIN_RIDERS_FLOOR = 5;
/** The hard bounds the floor enforces (the migration mirrors them). */
export const DRIVER_DEMAND_HINTS_BOUNDS = {
    windowMinutes: { min: 10, max: 180 },
    cellSizeMeters: { min: 300, max: 2_000 },
    minDistinctRiders: { min: DEMAND_HINTS_MIN_RIDERS_FLOOR, max: 100 },
    maxZones: { min: 1, max: 40 },
    radiusMeters: { min: 1_000, max: 30_000 },
    refreshSeconds: { min: 60, max: 900 },
    nudgeAfterOnlineMinutes: { min: 60, max: 720 },
    repeatEveryMinutes: { min: 15, max: 360 },
};
/** What an ungoverned market reads: zones from the last half hour in 500 m cells with at least five different riders; a break suggestion after four hours, hourly after that. */
export const DEFAULT_DRIVER_DEMAND_HINTS = {
    hotZones: { enabled: true, windowMinutes: 30, cellSizeMeters: 500, minDistinctRiders: 5, maxZones: 12, radiusMeters: 8_000, refreshSeconds: 120 },
    shiftHealth: { enabled: true, nudgeAfterOnlineMinutes: 240, repeatEveryMinutes: 60 },
};
/** What an unreadable version reads: no zones at all, and the wellbeing nudge on. */
export const STRICT_DRIVER_DEMAND_HINTS = {
    hotZones: { ...DEFAULT_DRIVER_DEMAND_HINTS.hotZones, enabled: false, cellSizeMeters: 1_000, minDistinctRiders: 20 },
    shiftHealth: DEFAULT_DRIVER_DEMAND_HINTS.shiftHealth,
};
export const DRIVER_DEMAND_HINTS_REASON_CODES = ['DRIVER_DEMAND_HINTS_GOVERNED', 'DRIVER_DEMAND_HINTS_DEFAULT', 'DRIVER_DEMAND_HINTS_INVALID'];
/** The stable reasons the hints record: the zones were not shown because too few riders asked, and the nudge came due. */
export const DEMAND_HINTS_K_ANONYMITY = 'DEMAND_HINTS_K_ANONYMITY';
export const SHIFT_HEALTH_LONG_ONLINE = 'SHIFT_HEALTH_LONG_ONLINE';
const MARKET_ID = /^[a-z]{2}:[a-z0-9]+(?:-[a-z0-9]+)*$/;
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const intIn = (v, min, max) => typeof v === 'number' && Number.isSafeInteger(v) && v >= min && v <= max;
const exactly = (o, members) => Object.keys(o).every((k) => members.includes(k)) && members.every((m) => m in o);
const ZONE_MEMBERS = ['enabled', 'windowMinutes', 'cellSizeMeters', 'minDistinctRiders', 'maxZones', 'radiusMeters', 'refreshSeconds'];
const SHIFT_MEMBERS = ['enabled', 'nudgeAfterOnlineMinutes', 'repeatEveryMinutes'];
/** The stable issue of a value that is not a demand-hints value, or undefined when it is one. */
export function driverDemandHintsIssue(v) {
    if (!isObj(v))
        return 'NOT_AN_OBJECT';
    if (!exactly(v, ['hotZones', 'shiftHealth']))
        return 'UNKNOWN_OR_MISSING_MEMBER';
    const z = v.hotZones;
    const s = v.shiftHealth;
    if (!isObj(z) || !isObj(s))
        return 'INVALID_SECTION';
    if (!exactly(z, ZONE_MEMBERS) || !exactly(s, SHIFT_MEMBERS))
        return 'UNKNOWN_OR_MISSING_MEMBER';
    const b = DRIVER_DEMAND_HINTS_BOUNDS;
    if (typeof z.enabled !== 'boolean')
        return 'INVALID_HOT_ZONES_ENABLED';
    if (!intIn(z.windowMinutes, b.windowMinutes.min, b.windowMinutes.max))
        return 'INVALID_WINDOW';
    if (!intIn(z.cellSizeMeters, b.cellSizeMeters.min, b.cellSizeMeters.max))
        return 'INVALID_CELL_SIZE';
    if (!intIn(z.minDistinctRiders, b.minDistinctRiders.min, b.minDistinctRiders.max))
        return 'INVALID_MIN_DISTINCT_RIDERS';
    if (!intIn(z.maxZones, b.maxZones.min, b.maxZones.max))
        return 'INVALID_MAX_ZONES';
    if (!intIn(z.radiusMeters, b.radiusMeters.min, b.radiusMeters.max))
        return 'INVALID_RADIUS';
    if (!intIn(z.refreshSeconds, b.refreshSeconds.min, b.refreshSeconds.max))
        return 'INVALID_REFRESH';
    if (typeof s.enabled !== 'boolean')
        return 'INVALID_SHIFT_HEALTH_ENABLED';
    if (!intIn(s.nudgeAfterOnlineMinutes, b.nudgeAfterOnlineMinutes.min, b.nudgeAfterOnlineMinutes.max))
        return 'INVALID_NUDGE_AFTER';
    if (!intIn(s.repeatEveryMinutes, b.repeatEveryMinutes.min, b.repeatEveryMinutes.max))
        return 'INVALID_NUDGE_REPEAT';
    return undefined;
}
export function validDriverDemandHintsValue(v) {
    return driverDemandHintsIssue(v) === undefined;
}
/** `driver.demandHints@<scope>:<ref>#v<n>`. */
export function driverDemandHintsVersionOf(scope, scopeRef, version) {
    return `${DRIVER_DEMAND_HINTS_KEY}@${scope}:${scopeRef}#v${version}`;
}
/** Build a valid version (country, state or market scope). Throws a RangeError on a malformed scope ref or value. */
export function driverDemandHintsSetting(input) {
    if (!settingScopeRefValid(input.scope, input.scopeRef)) {
        throw new RangeError(`a ${input.scope}-scope demand-hints version needs a valid ${SCOPE_REF_KIND[input.scope]}, got ${input.scopeRef}`);
    }
    const issue = driverDemandHintsIssue(input.value);
    if (issue !== undefined)
        throw new RangeError(`invalid demand-hints policy: ${issue}`);
    const { hotZones: z, shiftHealth: s } = input.value;
    return policySetting({
        key: DRIVER_DEMAND_HINTS_KEY,
        scope: input.scope,
        scopeRef: input.scopeRef,
        value: {
            hotZones: { enabled: z.enabled, windowMinutes: z.windowMinutes, cellSizeMeters: z.cellSizeMeters, minDistinctRiders: z.minDistinctRiders, maxZones: z.maxZones, radiusMeters: z.radiusMeters, refreshSeconds: z.refreshSeconds },
            shiftHealth: { enabled: s.enabled, nudgeAfterOnlineMinutes: s.nudgeAfterOnlineMinutes, repeatEveryMinutes: s.repeatEveryMinutes },
        },
        version: input.version,
        effectiveFrom: input.effectiveFrom,
        ...(input.effectiveTo !== undefined ? { effectiveTo: input.effectiveTo } : {}),
        mode: 'OVERRIDABLE',
    });
}
/** A lower scope's value bounded by one layer above it: privacy only goes up, the wellbeing nudge only gets earlier and more often. */
function tightened(own, above) {
    return {
        hotZones: {
            ...own.hotZones,
            enabled: own.hotZones.enabled && above.hotZones.enabled,
            cellSizeMeters: Math.max(own.hotZones.cellSizeMeters, above.hotZones.cellSizeMeters),
            minDistinctRiders: Math.max(own.hotZones.minDistinctRiders, above.hotZones.minDistinctRiders),
        },
        shiftHealth: {
            enabled: own.shiftHealth.enabled || above.shiftHealth.enabled,
            nudgeAfterOnlineMinutes: Math.min(own.shiftHealth.nudgeAfterOnlineMinutes, above.shiftHealth.nudgeAfterOnlineMinutes),
            repeatEveryMinutes: Math.min(own.shiftHealth.repeatEveryMinutes, above.shiftHealth.repeatEveryMinutes),
        },
    };
}
/**
 * What `marketId` shows a provider at `at`: the most specific of the country, the state and the market decides, then every layer above bounds it
 * ({@link tightened}). Nothing governing reads the default; a version that cannot be read or parsed reads the strict value. `regionCode` is the market's
 * registered state (ADR-0124 - a market with none skips the layer).
 */
export function resolveDriverDemandHints(settings, marketId, at, regionCode) {
    const strict = { marketId, value: STRICT_DRIVER_DEMAND_HINTS, reasonCode: 'DRIVER_DEMAND_HINTS_INVALID', policyVersion: `${DRIVER_DEMAND_HINTS_KEY}@invalid` };
    if (!MARKET_ID.test(marketId))
        return strict;
    const ctx = marketSettingContext(marketId, regionCode);
    const country = ctx.country;
    const relevant = settings.filter((s) => s.key === DRIVER_DEMAND_HINTS_KEY && (s.scope === 'country' || s.scope === 'region' || s.scope === 'market'));
    const resolution = resolvePolicy(relevant, ctx, DRIVER_DEMAND_HINTS_KEY, at);
    if (resolution.reason === 'NOT_SET') {
        return { marketId, value: DEFAULT_DRIVER_DEMAND_HINTS, reasonCode: 'DRIVER_DEMAND_HINTS_DEFAULT', policyVersion: `${DRIVER_DEMAND_HINTS_KEY}@default` };
    }
    if (resolution.scope === undefined || resolution.version === undefined || (resolution.scope !== 'country' && resolution.scope !== 'region' && resolution.scope !== 'market'))
        return strict;
    if (!validDriverDemandHintsValue(resolution.value))
        return strict;
    const ref = settingScopeRefIn(resolution.scope, ctx) ?? country;
    let value = resolution.value;
    for (const above of ancestorLayers(relevant, ctx, DRIVER_DEMAND_HINTS_KEY, at, resolution.scope)) {
        if (!validDriverDemandHintsValue(above.resolution.value))
            return strict;
        value = tightened(value, above.resolution.value);
    }
    const snap = settingSnapshotFields(relevant, DRIVER_DEMAND_HINTS_KEY, resolution.scope, ref, resolution.version);
    return {
        marketId,
        value,
        reasonCode: 'DRIVER_DEMAND_HINTS_GOVERNED',
        policyVersion: driverDemandHintsVersionOf(resolution.scope, ref, resolution.version),
        ...(snap.contentHash !== undefined ? { contentHash: snap.contentHash } : {}),
        ...(snap.changeId !== undefined ? { changeId: snap.changeId } : {}),
    };
}
export function driverDemandHintsWire(decision) {
    const { hotZones: z, shiftHealth: s } = decision.value;
    return {
        hotZones: { enabled: z.enabled, refreshSeconds: z.refreshSeconds, cellSizeMeters: z.cellSizeMeters },
        shiftHealth: { enabled: s.enabled, nudgeAfterOnlineSeconds: s.nudgeAfterOnlineMinutes * 60, repeatEverySeconds: s.repeatEveryMinutes * 60 },
        policyVersion: decision.policyVersion,
    };
}
//# sourceMappingURL=driver-demand-hints-policy.js.map